/**
 * FahrzeugAlertsListe — Dashboard-Widget: offene Fahrzeug-Fristenwarnungen
 *
 * Zeigt alle nicht-quittierten Alerts aus `fahrzeug_alerts` sortiert nach
 * Priorität (dringend > hoch > mittel), dann faellig_am aufsteigend.
 * Inline-Quittieren via optimistic update + /api/fahrzeug-alerts.
 *
 * Issue #1016 — Teil von #181
 */

import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, CheckCheck, Loader2, Car } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FahrzeugInfo {
  marke: string | null;
  modell: string | null;
  kennzeichen: string | null;
}

export interface FahrzeugAlertRow {
  id: string;
  fahrzeug_id: string;
  alert_typ: string;
  faellig_am: string;
  vorlauf_tage: number;
  ausgeloest_am: string;
  im_briefing: boolean;
  fahrzeuge: FahrzeugInfo | FahrzeugInfo[] | null;
}

function getFahrzeugInfo(fahrzeuge: FahrzeugInfo | FahrzeugInfo[] | null): FahrzeugInfo | null {
  if (!fahrzeuge) return null;
  return Array.isArray(fahrzeuge) ? (fahrzeuge[0] ?? null) : fahrzeuge;
}

export type AlertPrioritaet = 'dringend' | 'hoch' | 'mittel';

// ─── Pure helpers (exported for unit tests) ───────────────────────────────────

export function prioritaetFuerWidget(vorlaufTage: number): AlertPrioritaet {
  if (vorlaufTage <= 14) return 'dringend';
  if (vorlaufTage <= 30) return 'hoch';
  return 'mittel';
}

const PRIORITAET_RANK: Record<AlertPrioritaet, number> = {
  dringend: 3,
  hoch: 2,
  mittel: 1,
};

export function sortAlerts(alerts: FahrzeugAlertRow[]): FahrzeugAlertRow[] {
  return [...alerts].sort((a, b) => {
    const pa = PRIORITAET_RANK[prioritaetFuerWidget(a.vorlauf_tage)];
    const pb = PRIORITAET_RANK[prioritaetFuerWidget(b.vorlauf_tage)];
    if (pb !== pa) return pb - pa;
    return a.faellig_am.localeCompare(b.faellig_am);
  });
}

export const BADGE_COLORS: Record<AlertPrioritaet, string> = {
  dringend: 'bg-red-100 text-red-700 border-red-200',
  hoch: 'bg-orange-100 text-orange-700 border-orange-200',
  mittel: 'bg-yellow-50 text-yellow-700 border-yellow-200',
};

const ALERT_TYP_LABELS: Record<string, string> = {
  hu: 'HU',
  laufzeitende: 'Laufzeitende',
  versicherung: 'Versicherung',
};

// ─── Data fetching ─────────────────────────────────────────────────────────────

function useOpenAlerts() {
  const [alerts, setAlerts] = useState<FahrzeugAlertRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const { data, error } = await supabase
        .from('fahrzeug_alerts')
        .select('id, fahrzeug_id, alert_typ, faellig_am, vorlauf_tage, ausgeloest_am, im_briefing, fahrzeuge(marke, modell, kennzeichen)')
        .is('quittiert_am', null)
        .limit(50);
      if (error) {
        setFetchError(error.message);
      } else if (data) {
        setAlerts(sortAlerts(data as unknown as FahrzeugAlertRow[]));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { alerts, setAlerts, loading, fetchError, reload: fetch };
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function PrioritaetBadge({ vorlaufTage }: { vorlaufTage: number }) {
  const prioritaet = prioritaetFuerWidget(vorlaufTage);
  const label = prioritaet === 'dringend' ? 'Dringend' : prioritaet === 'hoch' ? 'Hoch' : 'Mittel';
  return (
    <span
      className={`text-[10px] font-bold px-1.5 py-0.5 rounded border shrink-0 ${BADGE_COLORS[prioritaet]}`}
      data-testid={`alert-badge-${prioritaet}`}
    >
      {label}
    </span>
  );
}

function TypBadge({ typ }: { typ: string }) {
  return (
    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 shrink-0">
      {ALERT_TYP_LABELS[typ] ?? typ}
    </span>
  );
}

function formatFaelligAm(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function FahrzeugLabel({ fz: raw }: { fz: FahrzeugAlertRow['fahrzeuge'] }) {
  const fz = getFahrzeugInfo(raw);
  if (!fz) return <span className="text-gray-400 text-xs">Fahrzeug unbekannt</span>;
  const name = [fz.marke, fz.modell].filter(Boolean).join(' ') || '—';
  return (
    <span className="flex items-center gap-1.5 min-w-0">
      <span className="text-xs font-semibold text-gray-800 truncate">{name}</span>
      {fz.kennzeichen && (
        <span className="text-[10px] font-mono text-gray-400 shrink-0">{fz.kennzeichen}</span>
      )}
    </span>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

export default function FahrzeugAlertsListe() {
  const { session } = useAuth();
  const { alerts, setAlerts, loading, fetchError, reload } = useOpenAlerts();
  const [quittierend, setQuittierend] = useState<Set<string>>(new Set());

  const handleQuittieren = useCallback(async (alertId: string) => {
    // Optimistic: remove from list immediately
    setAlerts(prev => prev.filter(a => a.id !== alertId));
    setQuittierend(prev => new Set(prev).add(alertId));

    try {
      const res = await fetch('/api/fahrzeug-alerts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ action: 'quittieren', alert_id: alertId }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Unbekannter Fehler' }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      toast.success('Alert quittiert');
    } catch (err: any) {
      toast.error(`Quittieren fehlgeschlagen: ${err?.message ?? 'Unbekannter Fehler'}`);
      reload(); // Restore correct state from DB
    } finally {
      setQuittierend(prev => {
        const next = new Set(prev);
        next.delete(alertId);
        return next;
      });
    }
  }, [session, setAlerts, reload]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8" data-testid="fahrzeug-alerts-liste">
        <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div
        className="flex flex-col items-center justify-center py-8 gap-2"
        data-testid="alerts-error-state"
      >
        <AlertTriangle className="w-8 h-8 text-red-200" />
        <p className="text-xs text-red-400">Fehler beim Laden der Alerts</p>
      </div>
    );
  }

  if (alerts.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center py-8 gap-2"
        data-testid="alerts-empty-state"
      >
        <CheckCheck className="w-8 h-8 text-gray-200" />
        <p className="text-xs text-gray-400">Keine offenen Fahrzeug-Alerts</p>
      </div>
    );
  }

  return (
    <div className="space-y-0.5" data-testid="fahrzeug-alerts-liste">
      {alerts.map(alert => {
        const isLoading = quittierend.has(alert.id);
        return (
          <div
            key={alert.id}
            className="flex items-center gap-2 py-2.5 px-2 rounded-lg hover:bg-gray-50 transition-colors"
            data-testid={`alert-row-${alert.id}`}
          >
            {/* Priority badge */}
            <PrioritaetBadge vorlaufTage={alert.vorlauf_tage} />

            {/* Typ badge */}
            <TypBadge typ={alert.alert_typ} />

            {/* Vehicle + date */}
            <div className="flex-1 min-w-0">
              <FahrzeugLabel fz={alert.fahrzeuge} />
              <div className="flex items-center gap-1 mt-0.5">
                <AlertTriangle className="w-3 h-3 text-gray-300 shrink-0" />
                <span className="text-[10px] text-gray-400">
                  fällig {formatFaelligAm(alert.faellig_am)}
                  {' · '}{alert.vorlauf_tage} Tage
                </span>
              </div>
            </div>

            {/* Quittieren button */}
            <button
              onClick={() => handleQuittieren(alert.id)}
              disabled={isLoading}
              className="shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold border border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-green-700 hover:border-green-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="btn-quittieren"
              aria-label={`Alert quittieren: ${ALERT_TYP_LABELS[alert.alert_typ] ?? alert.alert_typ}`}
            >
              {isLoading
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <CheckCheck className="w-3 h-3" />
              }
              <span className="hidden sm:inline">Quittieren</span>
            </button>
          </div>
        );
      })}

      <div className="mt-2 pt-2 border-t border-gray-50 flex items-center gap-1 text-[10px] text-gray-400">
        <Car className="w-3 h-3 shrink-0" />
        {alerts.length} offene {alerts.length === 1 ? 'Warnung' : 'Warnungen'}
      </div>
    </div>
  );
}
